import React from 'react';
import { Box, Paper, Checkbox, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Message from './messages/Message';
import { copyToClipboard } from '../utils/clipboard';

const AUDIO_MESSAGE_TYPES = new Set(['AUDIO', 'audio', 'VOICE', 'voice']);

const isAudioMessage = (message) => {
  if (AUDIO_MESSAGE_TYPES.has(message.message_type) || AUDIO_MESSAGE_TYPES.has(message.type)) {
    return true;
  }

  if (message.media?.is_voice_message || message.media?.isVoiceMessage) {
    return true;
  }

  return Boolean(message.media?.mime_type?.startsWith('audio/') || message.media?.mimeType?.startsWith('audio/'));
};

const ChatMessage = ({ msg, messageRef, scrollToMessage, selectionMode, isSelected, onCopied, grouped = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const handleCopy = async () => {
    const ok = await copyToClipboard(msg.text);
    if (ok && onCopied) onCopied();
  };

  return (
    <Box
      key={msg.id}
      ref={messageRef}
      sx={{
        display: 'flex',
        justifyContent: msg.isSent ? 'flex-end' : 'flex-start',
        alignItems: 'center',
        gap: 0.5,
        // Mensagens seguidas do mesmo remetente ficam mais próximas, como no
        // WhatsApp — só a última de uma sequência tem o espaço maior.
        mb: grouped ? 0.5 : 2,
        animation: 'fadeIn 0.3s ease-in',
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '&:hover .msg-copy-btn': { opacity: 1 },
      }}
    >
      {isDesktop && (
        <IconButton
          className="msg-copy-btn"
          size="small"
          title={t('chat.copyMessage')}
          onClick={handleCopy}
          sx={{ opacity: 0, transition: 'opacity 0.15s', order: msg.isSent ? 0 : 2 }}
        >
          <CopyIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      )}
      <Paper
        // Recebida com elevação 1: no dark o MUI só clareia o Paper pela elevação
        // (com 0 fica da cor da página, preto). A sombra é trocada por uma linha
        // sutil embaixo (como o WhatsApp), então o light não muda.
        elevation={msg.isSent ? 0 : 1}
        sx={(theme) => {
          const palette = (theme.vars || theme).palette;
          return {
            p: 1.5,
            width: isAudioMessage(msg) ? { xs: 'min(380px, calc(100vw - 64px))', sm: '380px' } : 'auto',
            maxWidth: isAudioMessage(msg) ? '100%' : '75%',
            order: 1,
            // Enviada no tom suave do primary (primarySoft), recebida no papel.
            bgcolor: isSelected
              ? (msg.isSent ? palette.primarySoft.dark : theme.alpha(palette.primary.main, 0.24))
              : (msg.isSent ? palette.primarySoft.main : palette.background.paper),
            color: msg.isSent ? palette.primarySoft.contrastText : palette.text.primary,
            boxShadow: `0 1px 0.5px ${palette.divider}`,
            borderRadius: msg.isSent ? '12px 2px 12px 12px' : '2px 12px 12px 12px',
            userSelect: 'text',
            WebkitUserSelect: 'text',
            transition: 'all 0.2s',
          };
        }}
      >
        {selectionMode && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
            <Checkbox
              size="small"
              checked={isSelected}
              sx={{ p: 0, mr: 1 }}
            />
          </Box>
        )}
        <Message message={msg} scrollToMessage={scrollToMessage} />
      </Paper>
    </Box>
  );
};

export default ChatMessage;
